import {
  PATH_SEPARATOR,
  pathLabelOf,
  pathOf,
  type Container,
  type ContainerItem,
  type Item,
} from "@egalregal/shared";

/**
 * How many characters of the matched identifier a suggestion shows. Three is
 * enough to recognise the code without repeating the whole UUID (scan-followups
 * 03) — the user still selects the suggestion instead of typing the rest.
 */
export const PREFIX_SHORT_LENGTH = 3;

/** One unique prefix match, ready to render and to resolve on selection. */
export interface PrefixMatch {
  kind: "container" | "item";
  /** The full code submitted when the suggestion is chosen. */
  code: string;
  name: string;
  /** Root-to-container path, or the item's placement path; null when unplaced. */
  path: string | null;
  /** First {@link PREFIX_SHORT_LENGTH} characters of the matched identifier. */
  shortCode: string;
}

/** The mirrored entities a typed prefix is matched against. */
export interface PrefixScope {
  containers: readonly Container[];
  items: readonly Item[];
  placements: readonly ContainerItem[];
}

/**
 * The single container or item whose identifier starts with the typed prefix,
 * or null when the prefix is empty, unknown or still ambiguous.
 *
 * Matching is case-insensitive and has no minimum length: a two character
 * prefix is enough when it is already unique, while a longer one waits until it
 * is. Containers match on their UUID, items on their barcode (product barcode
 * or QR tag) or their item UUID — the same identifiers the scanner resolves.
 * Ambiguity is answered with nothing, never with a guess (scan-followups 03).
 *
 * @param query the text typed into the manual field.
 * @param scope the loaded containers plus the browser item/placement mirror.
 * @returns the unique match, or null when there is none or more than one.
 */
export function uniquePrefixMatch(query: string, scope: PrefixScope): PrefixMatch | null {
  const prefix = query.trim().toLowerCase();
  if (prefix === "") return null;

  const matches: PrefixMatch[] = [];
  for (const container of scope.containers) {
    if (container.deleted) continue;
    if (!container.id.toLowerCase().startsWith(prefix)) continue;
    matches.push({
      kind: "container",
      code: container.id,
      name: container.name,
      // The name is shown separately; the path is its parent trail.
      path:
        pathOf(scope.containers, container.id)
          .slice(0, -1)
          .map((node) => node.name)
          .join(PATH_SEPARATOR) || null,
      shortCode: shortCodeOf(container.id),
    });
    if (matches.length > 1) return null;
  }

  for (const item of scope.items) {
    const barcodeHit = item.barcode.toLowerCase().startsWith(prefix);
    const idHit = item.id.toLowerCase().startsWith(prefix);
    if (!barcodeHit && !idHit) continue;
    // Both identifiers name the same item, so it is one match either way; the
    // barcode wins because it is the code the user is most likely reading.
    const code = barcodeHit ? item.barcode : item.id;
    matches.push({
      kind: "item",
      code,
      name: item.name,
      path: itemPlacementPath(item.id, scope),
      shortCode: shortCodeOf(code),
    });
    if (matches.length > 1) return null;
  }

  return matches[0] ?? null;
}

/** The first 3 characters of an identifier, e.g. `019` for a UUID label. */
function shortCodeOf(code: string): string {
  return code.slice(0, PREFIX_SHORT_LENGTH);
}

/**
 * The primary placement path of an item, or null when it is unplaced. With
 * several placements the lexicographically first path is shown, so the
 * suggestion is stable and never depends on mirror insertion order.
 */
function itemPlacementPath(itemId: string, scope: PrefixScope): string | null {
  const paths = scope.placements
    .filter((placement) => placement.itemId === itemId && placement.quantity > 0)
    .map((placement) => {
      const container = scope.containers.find((c) => c.id === placement.containerId);
      if (!container) return null;
      return pathLabelOf(scope.containers, container.id) || container.name;
    })
    .filter((entry): entry is string => entry !== null)
    .sort((a, b) => a.localeCompare(b));
  return paths[0] ?? null;
}
